import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { apiFetch } from "../../context/AuthContext";

const inputCls =
  "w-full mt-1.5 bg-white/5 border border-white/10 rounded-md px-4 py-3.5 text-base text-foreground placeholder:text-muted-foreground/50 focus:outline-none focus:border-primary/60 transition-colors duration-300";

export default function SetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [info, setInfo] = useState(null);
  const [error, setError] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (!token) {
      setError("This link is invalid or expired.");
      return;
    }
    apiFetch(`/auth/invite-info?token=${encodeURIComponent(token)}`)
      .then(setInfo)
      .catch((e) => setError(e.message));
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (password.length < 8) {
      setError("Password must be at least 8 characters.");
      return;
    }
    if (password !== confirm) {
      setError("Passwords do not match.");
      return;
    }
    setLoading(true);
    try {
      const path = info.purpose === "invite" ? "/auth/accept-invite" : "/auth/reset-password";
      await apiFetch(path, {
        method: "POST",
        body: JSON.stringify({ token, password }),
      });
      setDone(true);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      data-testid="set-password-page"
      className="min-h-screen bg-background text-foreground flex items-center justify-center px-6"
    >
      <div className="w-full max-w-sm">
        <Link to="/" className="block font-display text-3xl font-bold tracking-tight">
          CAN<span className="text-primary">ARTS</span>
        </Link>
        <p className="mt-3 text-xs uppercase tracking-[0.3em] text-secondary">
          Artist Discovery
        </p>

        {done ? (
          <div className="mt-8" data-testid="set-password-success">
            <h1 className="font-display font-bold tracking-tighter text-3xl">
              YOU'RE ALL SET.
            </h1>
            <p className="mt-3 text-sm font-light text-muted-foreground">
              Your password has been saved. You can now sign in to the admin area.
            </p>
            <Link
              to="/admin"
              data-testid="set-password-signin"
              className="mt-8 inline-flex items-center gap-3 bg-primary text-white px-8 py-4 rounded-full text-sm font-semibold uppercase tracking-[0.15em] hover:brightness-110 transition-[filter] duration-300"
            >
              Sign In <ArrowUpRight size={18} />
            </Link>
          </div>
        ) : error && !info ? (
          <div className="mt-8" data-testid="set-password-error">
            <h1 className="font-display font-bold tracking-tighter text-3xl">
              LINK EXPIRED
            </h1>
            <p className="mt-3 text-sm font-light text-muted-foreground">{error}</p>
            <Link
              to="/admin"
              className="mt-6 inline-block text-xs uppercase tracking-[0.2em] text-secondary hover:text-foreground transition-colors"
            >
              Go to sign in
            </Link>
          </div>
        ) : info ? (
          <>
            <h1 className="mt-8 font-display font-bold tracking-tighter text-4xl">
              {info.purpose === "invite" ? "CREATE YOUR PASSWORD" : "RESET PASSWORD"}
            </h1>
            <p className="mt-3 text-sm font-light text-muted-foreground">
              {info.name} · {info.email}
            </p>
            <form onSubmit={submit} className="mt-8 space-y-5">
              <div>
                <label className="text-xs uppercase tracking-[0.18em] text-muted-foreground">
                  New password
                </label>
                <input
                  data-testid="set-password-input"
                  type="password"
                  required
                  minLength={8}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className={inputCls}
                  placeholder="Min. 8 characters"
                />
              </div>
              <div>
                <label className="text-xs uppercase tracking-[0.18em] text-muted-foreground">
                  Confirm password
                </label>
                <input
                  data-testid="set-password-confirm"
                  type="password"
                  required
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  className={inputCls}
                />
              </div>
              {error && (
                <p data-testid="set-password-form-error" className="text-sm text-primary">
                  {error}
                </p>
              )}
              <button
                type="submit"
                data-testid="set-password-submit"
                disabled={loading}
                className="w-full inline-flex items-center justify-center gap-3 bg-primary text-white px-8 py-4 rounded-full text-sm font-semibold uppercase tracking-[0.15em] hover:brightness-110 transition-[filter] duration-300 disabled:opacity-60"
              >
                {loading ? "Saving..." : "Save password"}
                <ArrowUpRight size={18} />
              </button>
            </form>
          </>
        ) : (
          <p className="mt-8 text-sm text-muted-foreground">Loading...</p>
        )}
      </div>
    </div>
  );
}
